import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Globe, Check } from "lucide-react";
import {
  SUPPORTED_LOCALES,
  detectLocale,
  stripLocale,
  withLocale,
  DEFAULT_LOCALE,
  t,
} from "@/lib/i18n";

/**
 * Language selector · desktop header + mobile menu.
 *
 *   - All 10 locales are selectable. When a full translation of the current
 *     page exists (de/fr/es service pages) the switch lands on it; otherwise
 *     the English master content renders under the locale prefix with a
 *     canonical to the English URL (LocaleGate in App.js) — never a 404.
 *   - Current page/route is preserved on switch wherever possible.
 *   - Native label first, English label second where it differs, checkmark
 *     on the active locale.
 *   - Choice persisted to localStorage.
 */
export const LanguageSwitcher = ({ compact = false, dropUp = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const currentLocale = detectLocale(location.pathname);
  const canonicalPath = stripLocale(location.pathname);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (locale) => {
    try {
      localStorage.setItem("asia-sourcing-locale", locale);
    } catch (_) { /* non-critical */ }
    const target =
      locale === DEFAULT_LOCALE ? canonicalPath : withLocale(canonicalPath, locale);
    setOpen(false);
    navigate(target);
  };

  const current = SUPPORTED_LOCALES.find((l) => l.code === currentLocale);

  return (
    <div style={{ position: "relative" }} data-testid="language-switcher">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("lang.selector", currentLocale)}
        data-testid="language-switcher-button"
        className="lang-switcher-btn"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: ".4rem",
          padding: compact ? ".4rem .6rem" : ".45rem .8rem",
          background: "rgba(248, 250, 252, .07)",
          border: "1px solid rgba(248, 250, 252, .34)",
          borderRadius: 2,
          fontFamily: "var(--sans, inherit)",
          fontSize: ".75rem",
          color: "rgba(248, 250, 252, .92)",
          cursor: "pointer",
          lineHeight: 1,
          letterSpacing: ".05em",
          transition: "border-color 200ms, color 200ms, background-color 200ms",
        }}
      >
        <Globe size={14} strokeWidth={1.4} aria-hidden="true" />
        <span>{current?.nativeLabel || "English"}</span>
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          data-testid="language-switcher-menu"
          style={{
            position: "absolute",
            right: 0,
            // Mobile menu anchors the switcher near the bottom of the
            // viewport — open upward there so all 10 locales stay reachable.
            ...(dropUp ? { bottom: "calc(100% + 6px)" } : { top: "calc(100% + 6px)" }),
            minWidth: 210,
            maxHeight: "min(70vh, 460px)",
            overflowY: "auto",
            background: "var(--paper, #fff)",
            border: "1px solid var(--line, #d4cfc3)",
            boxShadow: "0 10px 30px rgba(26, 23, 20, .12)",
            padding: ".3rem 0",
            zIndex: 9000,
          }}
        >
          {SUPPORTED_LOCALES.map((loc) => {
            const isActive = loc.code === currentLocale;
            return (
              <button
                key={loc.code}
                type="button"
                role="menuitem"
                aria-current={isActive ? "true" : undefined}
                data-testid={`language-switcher-item-${loc.code}`}
                onClick={() => pick(loc.code)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "1rem",
                  width: "100%",
                  textAlign: "left",
                  padding: ".55rem .9rem",
                  background: isActive ? "var(--paper-raised, #f6f1e7)" : "transparent",
                  border: 0,
                  fontFamily: "var(--sans, inherit)",
                  fontSize: ".82rem",
                  color: "var(--ink)",
                  cursor: "pointer",
                }}
              >
                <span>
                  <span style={{ fontWeight: isActive ? 600 : 400 }}>{loc.nativeLabel}</span>
                  {loc.nativeLabel !== loc.label && (
                    <span
                      style={{
                        color: "var(--ink-soft, #9b8f7f)",
                        fontSize: ".7rem",
                        marginLeft: ".45rem",
                      }}
                    >
                      {loc.label}
                    </span>
                  )}
                </span>
                {isActive && <Check size={13} strokeWidth={1.8} aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
